import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import BackButton from '../../components/common/BackButton';

const ManageUsers = () => {
    const { t } = useTranslation();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [searchTerm, setSearchTerm] = useState('');


    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [totalPages, setTotalPages] = useState(0);
    const [totalUsers, setTotalUsers] = useState(0);


    const [showResetModal, setShowResetModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [newPassword, setNewPassword] = useState('');

    const [showAddUserModal, setShowAddUserModal] = useState(false);
    const [newUserData, setNewUserData] = useState({
        name: '',
        email: '',
        password: '',
        phoneNumber: '',
        personalNumber: '',
        role: 'user'
    });

    const [showEditRoleModal, setShowEditRoleModal] = useState(false);
    const [selectedUserRole, setSelectedUserRole] = useState('user');

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            fetchUsers();
        }, 500);
        return () => clearTimeout(timeoutId);
    }, [page, limit, searchTerm]);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const res = await axios.get('/api/auth/users', {
                params: {
                    page,
                    limit,
                    search: searchTerm
                }
            });
            setUsers(res.data.data.users);
            setTotalPages(res.data.totalPages);
            setTotalUsers(res.data.total);
            setLoading(false);
        } catch (err) {
            console.error(err);
            setError('Failed to fetch users');
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm(t('admin.confirmDeleteUser'))) {
            try {
                await axios.delete(`/api/auth/users/${id}`);
                setUsers(users.filter(user => user.id !== id));
                setSuccess('User deleted successfully');
                setTimeout(() => setSuccess(''), 3000);
            } catch (err) {
                setError(err.response?.data?.message || 'Failed to delete user');
                setTimeout(() => setError(''), 3000);
            }
        }
    };

    const openResetModal = (user) => {
        setSelectedUser(user);
        setNewPassword('');
        setShowResetModal(true);
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (!newPassword || newPassword.length < 8) {
            setError('Password must be at least 8 characters');
            setTimeout(() => setError(''), 3000);
            return;
        }

        try {
            await axios.patch(`/api/auth/users/${selectedUser.id}/reset-password`, {
                password: newPassword
            });
            setShowResetModal(false);
            setSuccess(`Password for ${selectedUser.name} reset successfully`);
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to reset password');
            setTimeout(() => setError(''), 3000);
        }
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setPage(1);
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        try {
            await axios.post('/api/auth/users', newUserData);
            setShowAddUserModal(false);
            setNewUserData({
                name: '',
                email: '',
                password: '',
                phoneNumber: '',
                personalNumber: '',
                role: 'user'
            });
            setSuccess(t('admin.userCreatedSuccess'));
            setTimeout(() => setSuccess(''), 3000);
            fetchUsers();
        } catch (err) {
            setError(err.response?.data?.message || t('admin.errorCreatingUser'));
            setTimeout(() => setError(''), 3000);
        }
    };

    const openEditRoleModal = (user) => {
        setSelectedUser(user);
        setSelectedUserRole(user.role);
        setShowEditRoleModal(true);
    };

    const handleUpdateRole = async (e) => {
        e.preventDefault();
        try {
            await axios.patch(`/api/auth/users/${selectedUser.id}/role`, {
                role: selectedUserRole
            });
            setShowEditRoleModal(false);
            setSuccess(t('admin.userRoleUpdatedSuccess'));
            setTimeout(() => setSuccess(''), 3000);
            fetchUsers();
        } catch (err) {
            setError(err.response?.data?.message || t('admin.errorUpdatingRole'));
            setTimeout(() => setError(''), 3000);
        }
    };

    return (
        <div className="container-fluid py-5 bg-light">
            <div className="container">
                <div className="d-flex justify-content-between align-items-center mb-5">
                    <h2 className="fw-bold text-primary">
                        <BackButton to="/admin" />

                        <i className="fas fa-users me-2"></i> {t('admin.manageUsersTitle') || 'User Management'}
                    </h2>
                    <button
                        className="btn btn-primary rounded-pill px-4"
                        onClick={() => setShowAddUserModal(true)}
                    >
                        <i className="fas fa-user-plus me-2"></i>
                        {t('admin.addUser')}
                    </button>
                </div>

                {error && (
                    <div className="alert alert-danger shadow-sm border-0" role="alert">
                        <i className="fas fa-exclamation-circle me-2"></i> {error}
                    </div>
                )}

                {success && (
                    <div className="alert alert-success shadow-sm border-0" role="alert">
                        <i className="fas fa-check-circle me-2"></i> {success}
                    </div>
                )}

                <div className="card border-0 shadow-sm bg-white">
                    <div className="card-header bg-white border-bottom-0 pt-4 px-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
                        <h5 className="card-title fw-bold mb-0 text-dark">{t('admin.usersList')}</h5>
                        <div className="d-flex gap-2 align-items-center flex-wrap">

                            <div className="position-relative" style={{ maxWidth: '300px', width: '100%' }}>
                                <input
                                    type="text"
                                    className="form-control ps-5 rounded-pill bg-light border-0"
                                    placeholder={t('admin.searchUser')}
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                />
                                <i className="fas fa-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                            </div>
                        </div>
                    </div>
                    <div className="card-body p-0">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : users.length === 0 ? (
                            <div className="text-center py-5">
                                <div className="text-muted">
                                    <i className="fas fa-users-slash fa-3x mb-3 opacity-50"></i>
                                    <p>{t('admin.noUsersFound')}</p>
                                </div>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-hover align-middle mb-0">
                                    <thead className="bg-light text-muted small text-uppercase">
                                        <tr>
                                            <th className="px-4 py-3 border-0">{t('admin.userName')}</th>
                                            <th className="px-4 py-3 border-0">{t('admin.userEmail')}</th>
                                            <th className="px-4 py-3 border-0">{t('admin.personalNumber')}</th>
                                            <th className="px-4 py-3 border-0">{t('admin.userRole')}</th>
                                            <th className="px-4 py-3 border-0">{t('admin.registrationDate')}</th>
                                            <th className="px-4 py-3 border-0 text-end">{t('admin.actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="border-top-0">
                                        {users.map((user) => (
                                            <tr key={user.id}>
                                                <td className="px-4 py-3 border-bottom-0">
                                                    <div className="d-flex align-items-center">
                                                        <div className="avatar rounded-circle bg-primary bg-opacity-10 text-primary d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px' }}>
                                                            <span className="fw-bold">{user.name.charAt(0).toUpperCase()}</span>
                                                        </div>
                                                        <span className="fw-bold text-dark">{user.name}</span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 border-bottom-0 text-muted">
                                                    <div>{user.email}</div>
                                                    <div className="small text-muted">{user.phoneNumber || '-'}</div>
                                                </td>
                                                <td className="px-4 py-3 border-bottom-0 text-muted">{user.personalNumber || '-'}</td>
                                                <td className="px-4 py-3 border-bottom-0">
                                                    <span className={`badge rounded-pill ${user.role === 'admin' ? 'bg-danger' : 'bg-success'}`}>
                                                        {user.role}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 border-bottom-0 text-muted">
                                                    {new Date(user.createdAt).toLocaleDateString()}
                                                </td>
                                                <td className="px-4 py-3 border-bottom-0 text-end">
                                                    <div className="btn-group">
                                                        <button
                                                            className="btn btn-sm btn-outline-primary"
                                                            onClick={() => openEditRoleModal(user)}
                                                            title={t('admin.editUserRole')}
                                                        >
                                                            <i className="fas fa-user-cog"></i>
                                                        </button>
                                                        {user.role !== 'admin' && (
                                                            <>
                                                                <button
                                                                    className="btn btn-sm btn-outline-warning ms-1"
                                                                    onClick={() => openResetModal(user)}
                                                                    title="Reset Password"
                                                                >
                                                                    <i className="fas fa-key"></i>
                                                                </button>
                                                                <button
                                                                    className="btn btn-sm btn-outline-danger ms-1"
                                                                    onClick={() => handleDelete(user.id)}
                                                                    title="Delete User"
                                                                >
                                                                    <i className="fas fa-trash-alt"></i>
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                    { }
                    <div className="card-footer bg-white border-top-0 py-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
                        <div className="d-flex align-items-center gap-2">
                            <span className="text-muted small">{t('admin.view')}</span>
                            <select
                                className="form-select form-select-sm"
                                style={{ width: '80px' }}
                                value={limit}
                                onChange={(e) => {
                                    setLimit(Number(e.target.value));
                                    setPage(1);
                                }}
                            >
                                <option value="10">10</option>
                                <option value="20">20</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                                <option value="500">500</option>
                            </select>
                            <span className="text-muted small">
                                {totalUsers > 0 ? (
                                    <>
                                        {t('admin.from')} {((page - 1) * limit) + 1} {t('admin.to')} {Math.min(page * limit, totalUsers)} {t('admin.of')} {totalUsers} {t('admin.users')}
                                    </>
                                ) : (
                                    <>0 {t('admin.users')}</>
                                )}
                            </span>
                        </div>

                        {totalPages > 1 && (
                            <nav aria-label="Page navigation">
                                <ul className="pagination pagination-sm mb-0">
                                    <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => setPage(p => Math.max(1, p - 1))}
                                            disabled={page === 1}
                                        >
                                            <i className="fas fa-chevron-right"></i>
                                        </button>
                                    </li>
                                    <li className="page-item disabled">
                                        <span className="page-link border-0 text-muted">
                                            {t('admin.pageInfo', { page, totalPages })}
                                        </span>
                                    </li>
                                    <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                            disabled={page >= totalPages}
                                        >
                                            <i className="fas fa-chevron-left"></i>
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        )}
                    </div>
                </div>
            </div>

            {/* Reset Password Modal */}
            {showResetModal && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-0 shadow">
                            <div className="modal-header border-bottom-0">
                                <h5 className="modal-title fw-bold">
                                    {t('admin.resetPasswordTitle', { name: selectedUser?.name })}
                                </h5>
                                <button type="button" className="btn-close" onClick={() => setShowResetModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <form onSubmit={handleResetPassword}>
                                    <div className="mb-3">
                                        <label className="form-label text-muted small">{t('admin.newPassword')}</label>
                                        <input
                                            type="text"
                                            className="form-control form-control-lg bg-light border-0"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            placeholder={t('admin.enterNewPassword')}
                                            minLength={8}
                                            required
                                        />
                                    </div>
                                    <div className="d-grid">
                                        <button type="submit" className="btn btn-primary btn-lg">{t('admin.submitReset')}</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Add User Modal */}
            {showAddUserModal && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-0 shadow">
                            <div className="modal-header border-bottom-0">
                                <h5 className="modal-title fw-bold">
                                    <i className="fas fa-user-plus me-2 text-primary"></i>
                                    {t('admin.addUserTitle')}
                                </h5>
                                <button type="button" className="btn-close" onClick={() => setShowAddUserModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <form onSubmit={handleAddUser}>
                                    <div className="mb-3">
                                        <label className="form-label text-muted small">{t('auth.fullName')}</label>
                                        <input
                                            type="text"
                                            className="form-control bg-light border-0"
                                            value={newUserData.name}
                                            onChange={(e) => setNewUserData({ ...newUserData, name: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label text-muted small">{t('auth.email')}</label>
                                        <input
                                            type="email"
                                            className="form-control bg-light border-0"
                                            value={newUserData.email}
                                            onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label text-muted small">{t('auth.password')}</label>
                                        <input
                                            type="password"
                                            className="form-control bg-light border-0"
                                            value={newUserData.password}
                                            onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
                                            minLength={8}
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label text-muted small">{t('auth.phoneNumber')}</label>
                                        <input
                                            type="text"
                                            className="form-control bg-light border-0"
                                            value={newUserData.phoneNumber}
                                            onChange={(e) => setNewUserData({ ...newUserData, phoneNumber: e.target.value })}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label text-muted small">{t('auth.personalNumber')}</label>
                                        <input
                                            type="text"
                                            className="form-control bg-light border-0"
                                            value={newUserData.personalNumber}
                                            onChange={(e) => setNewUserData({ ...newUserData, personalNumber: e.target.value })}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label text-muted small">{t('admin.userRole')}</label>
                                        <select
                                            className="form-select bg-light border-0"
                                            value={newUserData.role}
                                            onChange={(e) => setNewUserData({ ...newUserData, role: e.target.value })}
                                        >
                                            <option value="user">{t('admin.roleUser')}</option>
                                            <option value="admin">{t('admin.roleAdmin')}</option>
                                            <option value="reception">{t('admin.roleReception') || 'Reception'}</option>
                                            <option value="administration">{t('admin.roleAdministration') || 'Administration'}</option>
                                        </select>
                                    </div>
                                    <div className="d-grid">
                                        <button type="submit" className="btn btn-primary btn-lg">
                                            <i className="fas fa-plus me-2"></i>
                                            {t('admin.addUser')}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Role Modal */}
            {showEditRoleModal && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-0 shadow">
                            <div className="modal-header border-bottom-0">
                                <h5 className="modal-title fw-bold">
                                    <i className="fas fa-user-cog me-2 text-primary"></i>
                                    {t('admin.editUserRoleTitle', { name: selectedUser?.name })}
                                </h5>
                                <button type="button" className="btn-close" onClick={() => setShowEditRoleModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <form onSubmit={handleUpdateRole}>
                                    <div className="mb-3">
                                        <label className="form-label text-muted small">{t('admin.selectRole')}</label>
                                        <select
                                            className="form-select form-select-lg bg-light border-0"
                                            value={selectedUserRole}
                                            onChange={(e) => setSelectedUserRole(e.target.value)}
                                        >
                                            <option value="user">{t('admin.roleUser')}</option>
                                            <option value="admin">{t('admin.roleAdmin')}</option>
                                            <option value="reception">{t('admin.roleReception') || 'Reception'}</option>
                                            <option value="administration">{t('admin.roleAdministration') || 'Administration'}</option>
                                        </select>
                                    </div>
                                    <div className="d-grid">
                                        <button type="submit" className="btn btn-primary btn-lg">
                                            <i className="fas fa-save me-2"></i>
                                            {t('admin.updateRole')}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageUsers;
